import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { Navbar } from "./components/Navbar";
import { BrandingHead } from "./components/BrandingHead";
import { Footer } from "./components/Footer";
import { ScrollManager } from "./components/ScrollManager";
import { RedirectGuard } from "./components/RedirectGuard";
// Critical LCP routes stay eager — inlined into initial bundle for fastest paint.
import Home from "./pages/Home";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import NotFound from "./pages/NotFound";
// Non-critical routes are code-split (loaded on demand → smaller initial bundle,
// faster TTI + LCP on the home / category / product pages that matter for SEO).
const SearchPage = lazy(() => import("./pages/SearchPage"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const Page = lazy(() => import("./pages/Page"));
const WholesalePage = lazy(() => import("./pages/WholesalePage"));
const CubanWholesalePage = lazy(() => import("./pages/CubanWholesalePage"));
const GrillzWholesalePage = lazy(() => import("./pages/GrillzWholesalePage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const DriveConnected = lazy(() => import("./pages/admin/DriveConnected"));

// Minimal route fallback — no spinner shift, keeps CLS at 0.
const RouteFallback = () => <div style={{ minHeight: "60vh" }} aria-hidden="true" />;

const LowercaseRedirect = ({ children }) => {
  const { pathname } = useLocation();
  if (/[A-Z]/.test(pathname)) return <Navigate to={pathname.toLowerCase()} replace />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <WishlistProvider>
          <CartProvider>
            <BrowserRouter>
              <LowercaseRedirect>
                <ScrollManager />
                <RedirectGuard />
                <BrandingHead />
                <div className="flex min-h-screen w-full max-w-full flex-col overflow-x-clip bg-white">
                  <Navbar />
                  <main className="flex-1">
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/blog" element={<BlogList />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/cart" element={<CartPage />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/checkout/success" element={<CheckoutSuccess />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/auth/google" element={<GoogleCallback />} />
                        <Route path="/sitemap" element={<Sitemap />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/wishlist" element={<WishlistPage />} />
                        <Route path="/about" element={<Page slug="about" />} />
                        <Route path="/contact" element={<Page slug="contact" />} />
                        <Route path="/privacy" element={<Page slug="privacy" />} />
                        <Route path="/terms" element={<Page slug="terms" />} />
                        <Route path="/shipping" element={<Page slug="shipping" />} />
                        <Route path="/returns" element={<Page slug="returns" />} />
                        <Route path="/cancellation" element={<Page slug="cancellation" />} />
                        <Route path="/moissanite-watches-wholesale" element={<WholesalePage />} />
                        <Route path="/moissanite-cuban-chains-wholesale" element={<CubanWholesalePage />} />
                        <Route path="/moissanite-teeth-grillz-wholesale" element={<GrillzWholesalePage />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/drive-connected" element={<DriveConnected />} />
                        <Route path="/:categorySlug" element={<CategoryPage />} />
                        <Route path="/:categorySlug/:productSlug" element={<ProductPage />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                </div>
                <Toaster position="bottom-right" theme="light" />
              </LowercaseRedirect>
            </BrowserRouter>
          </CartProvider>
        </WishlistProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
